import { useQuery } from "@tanstack/react-query";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed, NothingYet } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { formatDate } from "@/components/table/cells";

/**
 * Settings → Logistics partners, statuses not recognised (#527, Part B).
 *
 * The courier statuses the translation table has no entry for, as received,
 * with how many scans carried each and when one was last seen. They are never
 * guessed: on a timeline each shows exactly as the courier wrote it with a
 * "Not recognised" mark, and none moves a shipment's stage. This list is how
 * the owner finds out one has started to turn up, and what to ask for.
 */
export function UnrecognisedStatuses() {
  const data = useDataSource();
  const query = useQuery({
    queryKey: ["tracking", "unrecognised"],
    queryFn: () => data.tracking.listUnrecognised(),
  });

  return (
    <section aria-labelledby="unrecognised-heading" className="space-y-3">
      <div className="max-w-prose">
        <h2 id="unrecognised-heading" className="text-base font-semibold">
          Statuses not recognised
        </h2>
        <p className="text-muted-foreground text-sm">
          Courier statuses the translation table has no entry for. They are
          shown exactly as received, marked “Not recognised”, and they never
          move a shipment&apos;s stage. Tell us what each one means and it is
          added to the table.
        </p>
      </div>

      {query.isPending ? (
        <TableSkeleton rows={2} />
      ) : query.isError ? (
        <LoadFailed
          message={query.error.message}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : query.data.length === 0 ? (
        <NothingYet
          title="Every status so far was recognised"
          description="If a courier starts sending one the table does not know, it appears here."
        />
      ) : (
        <>
          <div className="hidden overflow-x-auto rounded-md border md:block">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-muted/40">
                <tr className="border-b text-left">
                  <th className="px-3 py-2 font-medium">Status as received</th>
                  <th className="px-3 py-2 font-medium">Code</th>
                  <th className="px-3 py-2 font-medium">From</th>
                  <th className="px-3 py-2 text-right font-medium">
                    Times seen
                  </th>
                  <th className="px-3 py-2 font-medium whitespace-nowrap">
                    Last seen
                  </th>
                </tr>
              </thead>
              <tbody>
                {query.data.map((row) => (
                  <tr
                    key={`${row.source}|${row.status}|${row.code ?? ""}`}
                    className="border-b last:border-b-0"
                  >
                    <td className="px-3 py-2 font-mono text-xs wrap-anywhere">
                      {row.status !== "" ? (
                        row.status
                      ) : (
                        <span className="text-muted-foreground">
                          (no status, a code only)
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {row.code ?? (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {row.source === "shopify" ? "Shopify" : "Courier partner"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {row.count}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                      {formatDate(row.lastSeenAt, true)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="divide-y rounded-md border md:hidden">
            {query.data.map((row) => (
              <li
                key={`${row.source}|${row.status}|${row.code ?? ""}`}
                className="space-y-1 px-3 py-3"
              >
                <p className="font-mono text-sm wrap-anywhere">
                  {row.status !== "" ? row.status : "(no status, a code only)"}
                </p>
                <p className="text-muted-foreground text-sm">
                  {row.source === "shopify" ? "Shopify" : "Courier partner"}
                  {row.code ? ` · code ${row.code}` : ""}
                </p>
                <p className="text-sm tabular-nums">
                  Seen {row.count} {row.count === 1 ? "time" : "times"}, last{" "}
                  {formatDate(row.lastSeenAt, true)}
                </p>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
